class PredictiveSearch extends HTMLElement {
	constructor() {
		super();
		this.cachedResults = {};
		this.metaobjectCache = {};
		// Laufende Fetches je Cache-Key. Der Cache oben entsteht erst NACH der
		// Antwort — ohne diese Map liefe jeder Aufruf im Fenster dazwischen an
		// ihm vorbei (siehe fetchMetaobjectResults).
		this.metaobjectInflight = {};
		this.input = this.querySelector('input[type="search"]');
		this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
		this.isOpen = false;

		// Metaobject Search Backend (Teilenummern + Fehlercodes)
		this.metaobjectSearchEndpoint = 'https://repairfirstsuche.onrender.com/search';
		// Timeout für das Backend: Shopify-Treffer werden NIE dadurch blockiert
		this.metaobjectTimeoutMs = 2500;
		// Circuit-Breaker: nach einem Backend-Fehler so lange nicht erneut anfragen
		this.metaobjectRetryAfterMs = 60000;
		this.metaobjectDownUntil = 0;
		// Laufende Nummer der Suchanfrage — späte Antworten alter Queries
		// dürfen das Dropdown nicht mehr anfassen
		this.requestCounter = 0;

		// Submit-Weiche: NUR bei kaltem Cache fragen wir beim Absenden noch
		// einmal nach; danach übernimmt kompromisslos die native Suche
		// (Warm-Latenz des Backends gemessen: 72-167ms)
		this.exactMatchTimeoutMs = 800;
		// Schutz gegen doppeltes Enter im Wartefenster (zwei parallele Submits)
		this.submitPending = false;

		this.setupEventListeners();
	}

	setupEventListeners() {
		const form = this.querySelector('form.search');

		if (!form) {
			return;
		}

		form.addEventListener('submit', this.onFormSubmit.bind(this));
		this.select = this.querySelector('select');

		this.input.addEventListener('input', debounce((event) => {
			this.onChange(event);
		}, 300).bind(this));

		if (this.select) {
			this.select.addEventListener('input', debounce((event) => {
				this.onChange(event);
			}, 300).bind(this));
		}

		this.input.addEventListener('focus', this.onFocus.bind(this));
		this.addEventListener('focusout', this.onFocusOut.bind(this));
		this.addEventListener('keyup', this.onKeyup.bind(this));
		this.addEventListener('keydown', this.onKeydown.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	getSearchFilterType() {
		if (!this.select) {
			return '';
		}

		return this.select.dataset.type;
	}

	getSearchFilterTypeQuery() {
		if (!this.select) {
			return '';
		}

		return this.select.value.trim();
	}

	onChange() {
		const searchTerm = this.getQuery();

		if (!searchTerm.length) {
			this.close(true);
			return;
		}

		this.getSearchResults(searchTerm);
	}

	onFormSubmit(event) {
		// Unverändert: leere Query bzw. per Pfeiltaste markierter Eintrag
		// (dann navigiert selectOption() per Klick)
		if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
			event.preventDefault();
			return;
		}

		// Doppeltes Enter im Wartefenster: der erste Submit läuft schon
		if (this.submitPending) {
			event.preventDefault();
			return;
		}

		// Teilenummern und Fehlercodes sind Metaobjekte und für Shopifys native
		// Suche unsichtbar (/search?q=5K7035200K -> 0 Treffer). Bei GENAU EINEM
		// Exaktreffer leiten wir direkt auf die Metaobjekt-Seite. In JEDEM
		// anderen Fall — kein Exaktreffer, mehrdeutig, Breaker offen, Timeout,
		// Fehler — bleibt alles wie bisher: nativer Submit (fail-open).
		const searchTerm = this.getQuery();

		// Formwache: nur was wie eine Teilenummer aussieht, kommt überhaupt in
		// die Weiche. Textsuchen — auch mehrwortige mit Ziffer wie "Golf 7",
		// "Audi A3 8V" oder "W211 Tacho" — laufen damit ohne einen einzigen
		// Request und ohne eine einzige Millisekunde Wartezeit nativ durch.
		if (!this.looksLikeTeilenummer(searchTerm)) {
			return;
		}

		// Ist im header-select ein Filter (product_type/vendor) gesetzt, würde
		// ein Redirect ihn stillschweigend verwerfen -> dann nativ absenden.
		if (this.getSearchFilterTypeQuery() !== '') {
			return;
		}

		// Bewusste Redundanz zu fetchMetaobjectResults (das Cache-Hit, Breaker
		// und Fetch in EINEM Aufruf kapselt und nie rejectet): NUR ein
		// synchroner Cache-Blick erlaubt die Entscheidung, ganz ohne
		// preventDefault. Genau das hält den Normalfall byte-identisch zu
		// heute. Wer das hier als "doppelt" wegräumt, zerstört diese
		// Eigenschaft. Cache-Key identisch zu fetchMetaobjectResults.
		const cached = this.metaobjectCache[searchTerm.toLowerCase()];

		if (cached) {
			const url = this.findExactTeilenummer(cached, searchTerm);

			if (url) {
				event.preventDefault();
				// assign(), NICHT replace(): /search landet dank preventDefault
				// gar nicht erst in der History, der Zurück-Button muss auf die
				// Ausgangsseite führen. (Ein späterer /search-Fallback bräuchte
				// umgekehrt replace() — nicht vereinheitlichen.)
				window.location.assign(url);
			}

			// Kein Exaktreffer -> nativer Submit, exakt wie bisher
			return;
		}

		// Breaker offen (Backend tot/langsam): NICHT warten, sofort nativ.
		// Lieber 0 Treffer als eine tote Seite.
		if (Date.now() < this.metaobjectDownUntil) {
			return;
		}

		// Cache-Miss (= tippen und sofort Enter, der gemeldete Fall): einmal
		// nachfragen, hart gedeckelt.
		event.preventDefault();
		this.submitPending = true;

		const form = event.target;
		let settled = false;

		const goNative = () => {
			if (settled) return;
			settled = true;
			this.submitPending = false;
			clearTimeout(fallbackTimer);

			// Cancel-on-Interaction: hat der Nutzer im Wartefenster
			// weitergetippt (oder einen Dropdown-Link angeklickt und damit eine
			// Navigation gestartet), gehört das Feld ihm — form.submit() würde
			// den INZWISCHEN GEÄNDERTEN Wert absenden bzw. seine Navigation
			// überschreiben. Dann nur das Fenster schliessen; ein erneutes Enter
			// trifft den warmen Cache (0ms).
			if (this.getQuery() !== searchTerm) return;

			// form.submit() löst KEIN submit-Event aus -> keine Endlosschleife.
			// form.requestSubmit() WÜRDE es auslösen und darf hier nie stehen.
			// Kehrseite: ein künftig ergänzter Analytics-Listener am
			// Suchformular feuert auf diesem Fallback-Weg nicht.
			form.submit();
		};

		// Notbremse VOR dem try/catch scharfstellen: sie feuert auch dann, wenn
		// der Fetch-Aufbau synchron wirft oder eine Antwort trotz Abort nie
		// eintrifft. Der Nutzer wartet nie länger als exactMatchTimeoutMs.
		const fallbackTimer = setTimeout(goNative, this.exactMatchTimeoutMs);

		try {
			// Nutzt Cache, Circuit-Breaker und den 2500ms-Abort von oben.
			// Rejectet nie -> das .catch() ist reine Absicherung.
			this.fetchMetaobjectResults(searchTerm)
				.then((result) => {
					if (settled) return;

					const url = this.findExactTeilenummer(result.data, searchTerm);

					if (url) {
						settled = true;
						this.submitPending = false;
						clearTimeout(fallbackTimer);

						// Gleiche Cancel-on-Interaction-Regel wie in goNative():
						// wer weitergetippt hat, will nicht ungefragt auf die
						// Seite der ALTEN Query geworfen werden.
						if (this.getQuery() !== searchTerm) return;

						window.location.assign(url);
					} else {
						goNative();
					}
				})
				.catch(goNative);
		} catch (error) {
			goNative();
		}
	}

	onFocus() {
		const searchTerm = this.getQuery();

		if (!searchTerm.length) {
			return;
		}

		if (this.getAttribute('results') === 'true') {
			this.open();
		} else {
			this.getSearchResults(searchTerm);
		}
	}

	onFocusOut() {
		setTimeout(() => {
			if (!this.contains(document.activeElement)) {
				this.close();
			}
		});
	}

	onKeyup(event) {
		if (!this.getQuery().length) {
			this.close(true);
		}

		event.preventDefault();

		switch (event.code) {
			case 'ArrowUp':
				this.switchOption('up')
				break;
			case 'ArrowDown':
				this.switchOption('down');
				break;
			case 'Enter':
				this.selectOption();
				break;
		}
	}

	onKeydown(event) {
		if (
			event.code === 'ArrowUp' ||
			event.code === 'ArrowDown'
		) {
			event.preventDefault();
		}
	}

	switchOption(direction) {
		if (!this.getAttribute('open')) {
			return;
		}

		const moveUp = direction === 'up';
		const selectedElement = this.querySelector('[aria-selected="true"]');

		const allVisibleElements = Array.from(
			this.querySelectorAll("li, button.predictive-search-item-button")
		).filter((element) => element.offsetParent !== null);
		let activeElementIndex = 0;

		if (moveUp && !selectedElement) return;

		let selectedElementIndex = -1;
		let i = 0;

		while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
			if (allVisibleElements[i] === selectedElement) {
				selectedElementIndex = i;
			}
			i++;
		}

		this.statusElement.textContent = "";

		if (!moveUp && selectedElement) {
			activeElementIndex =
				selectedElementIndex === allVisibleElements.length - 1
				? 0
				: selectedElementIndex + 1;
		} else if (moveUp) {
			activeElementIndex =
				selectedElementIndex === 0
				? allVisibleElements.length - 1
				: selectedElementIndex - 1;
		}

		if (activeElementIndex === selectedElementIndex) return;

		const activeElement = allVisibleElements[activeElementIndex];

		activeElement.setAttribute('aria-selected', true);
		if (selectedElement) selectedElement.setAttribute('aria-selected', false);

		this.input.setAttribute('aria-activedescendant', activeElement.id);
	}

	selectOption() {
		const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

		if (selectedProduct) {
			selectedProduct.click();
		}
	}

	getSearchResults(searchTerm) {
		const filterType      = this.getSearchFilterType();
		const filterTypeQuery = this.getSearchFilterTypeQuery();
		const queryKey = searchTerm.replaceAll(" ", "-").toLowerCase() + '-' + filterType + '-' + filterTypeQuery.replaceAll(" ", "-").toLowerCase();
		const requestId = ++this.requestCounter;

		this.setLiveRegionLoadingState();

		const limit = this.dataset.limit;
		const limitScope = this.dataset.limitScope;
		const resultTypes = this.dataset.types;
		const showUnavailable = this.dataset.unavailable;
		const query = filterTypeQuery ? encodeURIComponent(`${filterType}:${filterTypeQuery}+* ${searchTerm}`) : encodeURIComponent(searchTerm);

		const shopifyPromise = this.cachedResults[queryKey]
			? Promise.resolve({ cached: true, markup: this.cachedResults[queryKey] })
			: fetch(`${routes.predictive_search_url}?q=${query}&resources[type]=${resultTypes}&resources[limit_scope]=${limitScope}&resources[limit]=${limit}&options[prefix]=last&options[unavailable_products]=${showUnavailable}&section_id=predictive-search`)
				.then((response) => {
					if (!response.ok) {
						throw new Error(response.status);
					}
					return response.text();
				})
				.then((text) => {
					const markup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
					return { markup };
				});

		const metaobjectPromise = this.fetchMetaobjectResults(searchTerm);

		// Shopify-Treffer werden SOFORT gerendert; die Metaobject-Spalte wird
		// nachgeschoben, sobald (und falls) das Backend antwortet. Früher
		// wartete hier ein Promise.all auf beide — als das Backend am 08.07.
		// ausfiel, hing das Dropdown dadurch für jede Eingabe 20-27s.
		shopifyPromise
			.then((shopifyResult) => {
				if (!shopifyResult.cached && shopifyResult.markup) {
					this.cachedResults[queryKey] = shopifyResult.markup;
				}

				if (requestId !== this.requestCounter) return;

				this.renderShopifyResults(shopifyResult.markup);

				return metaobjectPromise.then((metaobjectResult) => {
					if (requestId !== this.requestCounter) return;

					const metaobjectData = metaobjectResult.data;
					if (metaobjectData?.results?.length > 0) {
						this.appendMetaobjectResults(metaobjectData.results);
					}
				});
			})
			.catch((error) => {
				if (requestId === this.requestCounter) {
					this.close();
				}
				throw error;
			});
	}

	fetchMetaobjectResults(searchTerm) {
		const metaobjectKey = searchTerm.toLowerCase();

		if (this.metaobjectCache[metaobjectKey]) {
			return Promise.resolve({ cached: true, data: this.metaobjectCache[metaobjectKey] });
		}

		if (Date.now() < this.metaobjectDownUntil) {
			return Promise.resolve({ data: { results: [] } });
		}

		// In-flight-Dedup. Der Cache-Blick oben greift erst, wenn die Antwort da
		// ist; im Fenster davor (151ms warm, bis zu 2500ms am kalten Dyno) liefe
		// ein zweiter Aufruf mit demselben Key sonst an ihm vorbei und feuerte ein
		// ZWEITES identisches Request. Trifft den Bestand (zwei Instanzen, schnelles
		// Fokus-Wechseln) und seit dem /search-Vorschlagsblock den Regelfall: der
		// startet den Fetch beim Seitenaufbau, der Klick in das mit search.terms
		// vorbefüllte Feld folgt Millisekunden später. Genau dieser kalte Pfad hat
		// per Cold-Cache-Stampede schon einmal 5 Tage Totalausfall erzeugt.
		if (this.metaobjectInflight[metaobjectKey]) {
			return this.metaobjectInflight[metaobjectKey];
		}

		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), this.metaobjectTimeoutMs);

		const request = fetch(`${this.metaobjectSearchEndpoint}?q=${encodeURIComponent(searchTerm)}`, { signal: controller.signal })
			.then((response) => {
				if (!response.ok) throw new Error(response.status);
				return response.json();
			})
			.then((data) => {
				this.metaobjectCache[metaobjectKey] = data;
				return { data };
			})
			.catch((error) => {
				console.warn('[PredictiveSearch] Metaobject search failed:', error);
				this.metaobjectDownUntil = Date.now() + this.metaobjectRetryAfterMs;
				return { data: { results: [] } };
			})
			.finally(() => {
				clearTimeout(timeoutId);
				// Läuft NACH dem .then()/.catch() oben: der Cache-Eintrag steht
				// bereits bzw. der Breaker ist gesetzt — der nächste Aufruf fällt
				// also auf Cache oder Breaker, nie ins Leere.
				delete this.metaobjectInflight[metaobjectKey];
			});

		// Synchron VOR dem ersten .then() gesetzt -> ein Aufruf in derselben
		// Task sieht den Eintrag garantiert.
		this.metaobjectInflight[metaobjectKey] = request;

		return request;
	}

	// Formwache für die Submit-Weiche. Drei Bedingungen, alle nötig:
	//   1. JEDES durch Leerzeichen getrennte Token ist TN-förmig
	//      (nur [0-9a-z-]) UND enthält mindestens eine Ziffer. VW schreibt
	//      "3C0 035 680" — alle Gruppen ziffrig. "Golf 7" scheitert am Token
	//      "golf", "Audi A3 8V" an "audi", "MIB2 Main Unit" an "Main".
	//   2. Der normalisierte Key ist mindestens 5 Zeichen lang.
	//   3. Er enthält mindestens 4 Ziffern.
	// Zu (3): der teilenummer-Metaobjekt-Namensraum enthält auch Pseudo-TN
	// (Modellnamen: MIB2MainUnit, MIB2Entry, MIB2GP, DiscoverPro). Für die
	// liefert das Backend score===100 und exakte Namensgleichheit — alle
	// Schlösser in findExactTeilenummer() fallen, der Redirect würde feuern und
	// einen Seat-Kunden auf das VW-Gerät werfen. Echte Teilenummern haben >=8
	// Ziffern (5k7035200k, 3c0035680, a1402700861), die bekannten Pseudo-TN
	// <=1 — die Schwelle 4 trennt beide Klassen mit Sicherheitsabstand.
	// Wichtig: das ist eine FORM-Heuristik, kein Vertrag. Legt jemand im
	// Backend einen Modellnamen mit vier Ziffern als teilenummer an, kippt sie
	// wieder; sauber wäre ein eigener type für Pseudo-TN.
	// ZWEITER AUFRUFER: rfInitSearchPageSuggestions() am Dateiende nutzt diese
	// Wache als Request-Sparer für den /search-Vorschlagsblock. Wer die
	// Schwellen wegen einer Pseudo-TN anzieht, schrumpft LAUTLOS auch dessen
	// Abdeckung: "01130" liegt mit key.length=5 und 5 Ziffern exakt auf beiden
	// Schwellen, OBD-Codes wie P0299/U0100 mit exakt 4 Ziffern auf der zweiten.
	// Ein Test mit q=01130 ist der Stolperdraht dafür.
	looksLikeTeilenummer(searchTerm) {
		const tokens = String(searchTerm == null ? '' : searchTerm).trim().split(/\s+/);
		const tokenLooksLikeTn = tokens.every((token) => (
			/^[0-9a-z-]+$/i.test(token) && /\d/.test(token)
		));

		if (!tokenLooksLikeTn) {
			return false;
		}

		const key = this.normalizeSearchKey(searchTerm);
		const ziffern = (key.match(/\d/g) || []).length;

		return key.length >= 5 && ziffern >= 4;
	}

	// Zielseite NUR bei genau einem eindeutigen Exaktreffer, sonst null.
	// Score-Semantik von rf-search (services/fuzzy-search.js): 100 = exakter
	// Name, 95 = exaktes Handle, 90 = startsWith, 80 = contains, 70+ =
	// Levenshtein. Nur ===100 ist exakt; ein >=90-Tor würde den Golf-Kunden auf
	// eine Teilenummer-Seite werfen. score===100 ist aber eine Backend-
	// KONVENTION, kein Vertrag — deshalb die normalisierte Namensgleichheit als
	// zweites, lokales Schloss. exact.length === 1: bei zwei Exaktreffern
	// lieber native Suche als eine geratene Weiterleitung.
	findExactTeilenummer(data, searchTerm) {
		const results = (data && data.results) || [];
		const key = this.normalizeSearchKey(searchTerm);
		const exact = results.filter((item) => (
			item &&
			item.type === 'teilenummer' &&
			item.score === 100 &&
			this.normalizeSearchKey(item.name) === key &&
			this.isInternalUrl(item.url)
		));

		return exact.length === 1 ? exact[0].url : null;
	}

	// Spiegelt die Normalisierung des Backends (rf-search
	// services/fuzzy-search.js: normalize = .toLowerCase().replace(/[-\s]/g,'')).
	// Bewusst NICHT strenger — sonst weicht die lokale Gegenprobe vom Score des
	// Backends ab. Nötig, weil das Backend zu q="3C0 035 680" den Namen
	// "3C0035680" liefert: ein naives item.name === searchTerm würde die Weiche
	// still töten.
	normalizeSearchKey(value) {
		return String(value == null ? '' : value).toLowerCase().replace(/[-\s]/g, '');
	}

	// Nur interne Pfade — schützt vor //evil.com, falls das Backend je etwas
	// anderes liefert (vgl. den href="undefined"-Bug in buildMetaobjectSection:
	// das Backend hat schon einmal Unerwartetes geliefert). Die Auflösung läuft
	// über denselben Parser, den location.assign() nutzt: eine reine
	// Zeichenprüfung übersieht "/\evil.com" (der WHATWG-Parser behandelt den
	// Backslash bei http/https wie einen Slash -> https://evil.com/).
	isInternalUrl(url) {
		if (typeof url !== 'string' || url.charAt(0) !== '/') {
			return false;
		}

		try {
			return new URL(url, window.location.origin).origin === window.location.origin;
		} catch (error) {
			return false;
		}
	}

	renderShopifyResults(shopifyMarkup) {
		this.predictiveSearchResults.innerHTML = shopifyMarkup;

		this.setAttribute('results', true);
		this.setLiveRegionResults();
		this.open();
	}

	appendMetaobjectResults(results) {
		const resultsContainer = this.querySelector('#predictive-search-results');
		if (!resultsContainer) return;

		const teilenummern = results.filter(r => r.type === 'teilenummer');
		const fehlercodes = results.filter(r => r.type === 'fehlercodes');

		if (teilenummern.length === 0 && fehlercodes.length === 0) return;

		const hasShopifyResults = this.predictiveSearchResults.querySelectorAll('.predictive-search-list-item-product, .predictive-search-list-item-query, .predictive-search-list-item-collection').length > 0;

		// Only use side-by-side layout when both sides have results
		if (hasShopifyResults) {
			resultsContainer.classList.add('predictive-search-results--combined');
		}

		// Insert metaobject column as sibling of the Shopify wrapper (not inside it)
		const searchKeywords = resultsContainer.querySelector('#predictive-search-option-search-keywords');
		const metaobjectHTML = this.buildMetaobjectColumn(teilenummern, fehlercodes);

		if (searchKeywords) {
			searchKeywords.insertAdjacentHTML('beforebegin', metaobjectHTML);
		} else {
			resultsContainer.insertAdjacentHTML('beforeend', metaobjectHTML);
		}
	}

	buildMetaobjectColumn(teilenummern, fehlercodes) {
		let sectionsHTML = '';

		if (teilenummern.length > 0) {
			sectionsHTML += this.buildMetaobjectSection('Teilenummern', teilenummern, 'teilenummer');
		}

		if (fehlercodes.length > 0) {
			sectionsHTML += this.buildMetaobjectSection('Fehlercodes', fehlercodes, 'fehlercodes');
		}

		return `
			<div class="predictive-search-results-group predictive-search-results-group--metaobjects">
				${sectionsHTML}
			</div>
		`;
	}

	buildMetaobjectSection(title, items, type) {
		const esc = this.escapeHtml.bind(this);
		const listItems = items.map((item, index) => {
			// item.url kommt vom Backend (/pages/teilenummer/... bzw. /pages/fehlercodes/...);
			// item.product.url verlinkt aufs Produkt. Der frühere Zugriff auf ein
			// nie geliefertes product.url-Feld erzeugte href="undefined".
			const url = item.url || (item.product && item.product.url) || `/pages/${type}/${item.handle}`;
			return `
				<li
					id="predictive-search-option-${type}-${index}"
					class="predictive-search-list-item predictive-search-list-item--metaobject"
					role="option"
					aria-selected="false"
				>
					<a href="${esc(url)}" class="predictive-search-item predictive-search-item-link predictive-search-item--metaobject" tabindex="-1">
						<div class="predictive-search-item-content">
							<span class="predictive-search-item-type predictive-search-item-type--${type}">${esc(item.displayType)}</span>
							<h3 class="predictive-search-item-heading typography-body">${esc(item.name)}</h3>
							${item.product ? `<span class="predictive-search-item-vendor">${esc(item.product.title)}</span>` : ''}
						</div>
					</a>
				</li>
			`;
		}).join('');

		return `
			<div class="predictive-search-metaobject-section">
				<h3 class="predictive-search-heading predictive-search-heading--metaobject h5">${this.escapeHtml(title)}</h3>
				<ul class="predictive-search-results-item-list" role="group">
					${listItems}
				</ul>
			</div>
		`;
	}

	escapeHtml(value) {
		return String(value == null ? '' : value)
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	setLiveRegionLoadingState() {
		this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
		this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

		this.setLiveRegionText(this.loadingText);
		this.setAttribute('loading', true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute('aria-hidden', 'false');
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute('aria-hidden', 'true');
		}, 1000);
	}

	setLiveRegionResults() {
		this.removeAttribute('loading');
		const countEl = this.querySelector('[data-predictive-search-live-region-count-value]');
		if (countEl) {
			this.setLiveRegionText(countEl.textContent);
		}
	}

	getResultsMaxHeight() {
		this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
		return this.resultsMaxHeight;
	}

	open() {
		this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
		this.setAttribute('open', true);
		this.input.setAttribute('aria-expanded', true);

		if (!this.isOpen) {
			publish(PUB_SUB_EVENTS.searchResultsOpen);
		}

		this.isOpen = true;
	}

	close(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = '';
			this.removeAttribute('results');
		}

		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) {
			selected.setAttribute('aria-selected', false);
		}

		this.input.setAttribute('aria-activedescendant', '');
		this.removeAttribute('open');
		this.input.setAttribute('aria-expanded', false);
		this.resultsMaxHeight = false
		this.predictiveSearchResults.removeAttribute('style');

		if (this.isOpen) {
			publish(PUB_SUB_EVENTS.searchResultsClose);
		}

		this.isOpen = false;
	}
}

customElements.define('predictive-search', PredictiveSearch);

// ---------------------------------------------------------------------------
// Stufe 2: additiver Vorschlagsblock auf der Suchergebnis-Seite (/search)
// ---------------------------------------------------------------------------
// Die Submit-Weiche in onFormSubmit() oben deckt nur "im Feld tippen und Enter"
// ab. Bookmark, geteilter Link, Google-Treffer und die Browser-Autovervoll-
// ständigung laufen an ihr vorbei und landen direkt auf /search?q=5K7035200K ->
// "Keine Ergebnisse gefunden", obwohl /pages/teilenummer/5k7035200k existiert.
// Fehlercodes erreicht die Weiche prinzipiell NIE: deren Metaobjekt-Name ist
// Code + Beschreibung ("01130 - 046A ABS-Betrieb unplausibel / Unplausibles
// Signal"), das Backend liefert dafür score 90, nie 100 -> das ===100-Tor in
// findExactTeilenummer() kann nicht auslösen. Ein Redirect wäre dort auch
// inhaltlich falsch: 01130 betrifft MK20/MK26/MK60/MK61, also mehrere Geräte —
// eine LISTE ist die richtige Antwort, keine Weiterleitung.
//
// Deshalb hier ein ADDITIVER Block und KEIN location.replace(): die Seite, die
// der Kunde gerade liest, darf ihm nicht bis zu 2500ms später weggerissen
// werden.

// Nur >=90 = exakter Name (100) / exaktes Handle (95) / startsWith (90).
// Gemessen gegen das Live-Backend, und das ist der Grund für die Schwelle:
// q=01130 liefert 10 Treffer — EINEN brauchbaren (fehlercodes, 90) und NEUN
// teilenummer-Treffer mit score 80 ("contains"), reiner Substring-Müll
// (10096011303; 01136 = Honda Jazz; 110080113007 = Mercedes SLK Kombi-
// instrument). Ein ungefiltertes "Top 6" zeigte 1 Treffer + 5 Mercedes/Honda.
// Bei kurzen numerischen Queries ist der 80er-Rang systematisch Müll.
// Gegenprobe q=5K7035200K: 1x 100 + 7x 90 (5K7035200/A/B/C/... = echte
// Geschwister-TN desselben Produkts). Die 90 trennt beide Fälle mit EINER
// Regel — wer sie auf 80 dreht, holt den Honda in die ABS-Suche zurück.
// Wie schon das ===100-Tor der Submit-Weiche ist das eine KONVENTION von
// rf-search (services/fuzzy-search.js), kein Vertrag.
const RF_SUGGEST_MIN_SCORE = 90;
const RF_SUGGEST_MAX_ITEMS = 5;

// Badge-Klasse aus einer Whitelist, NICHT aus item.type interpoliert: item.type
// ist backend-gesteuert. Bei createElement/className ist das keine XSS-, aber
// eine Sauberkeits-/Debug-Frage.
const RF_SUGGEST_BADGE_CLASS = {
	teilenummer: 'predictive-search-item-type--teilenummer',
	fehlercodes: 'predictive-search-item-type--fehlercodes'
};

function rfInitSearchPageSuggestions() {
	// Der Anker IST das Gate: er rendert nur auf /search, nur bei
	// search.performed und nur wenn settings.predictive_search_enabled an ist.
	// Kein Pfad-Vergleich auf location.pathname — Shopify hat lokalisierte
	// Routen (/de/search), ein Pfad-Vergleich wäre schlicht falsch.
	const mount = document.getElementById('rf-metaobject-suggestions');
	if (!mount || mount.children.length) return;

	// search.terms aus Liquid statt URLSearchParams.get('q'): Liquid ist die
	// Quelle der Wahrheit dafür, wonach die Seite tatsächlich gesucht hat
	// (q kann Filter-Syntax tragen). Derselbe | escape-Weg wie Z.65/Z.145.
	const searchTerm = (mount.dataset.searchTerms || '').trim();
	if (!searchTerm) return;

	// Instanz-Vorzug statt blankem querySelector: es gibt VIER
	// <predictive-search>-Fundstellen im Theme (header.liquid:375,
	// header-search.liquid:28, header-search-custom.liquid:32,
	// main-search.liquid:50). Im 0-Treffer-Fall steht eine EIGENE Instanz in
	// .template-search — genau das Feld, in dem der Kunde als nächstes
	// korrigiert. Der Cache lebt pro Instanz (siehe Constructor), deshalb leihen
	// wir bevorzugt DIESE: der Fetch hier wärmt dann exakt ihren Cache
	// (Cache-Key = searchTerm.toLowerCase(), Liquid befüllt das Feld mit
	// demselben q). Klick ins Feld = 0 zusätzliche Requests; ein Enter trifft
	// den synchronen Cache-Blick der Submit-Weiche ohne Wartefenster.
	// Das gilt auch WÄHREND unser Fetch noch läuft — dafür sorgt die
	// In-flight-Dedup in fetchMetaobjectResults; ohne sie wäre der Klick ins
	// vorbefüllte Feld ein zweites Request auf genau dem kalten Pfad.
	const instance = document.querySelector('.template-search predictive-search')
		|| document.querySelector('predictive-search');

	// Ohne Instanz (Setting aus, Script-Fehler) passiert nichts. Der
	// typeof-Test schützt zusätzlich vor einem Bundle, das die Stufe-1-Methoden
	// nicht enthält (siehe Warnung in theme.liquid).
	if (!instance || typeof instance.fetchMetaobjectResults !== 'function') return;

	// Formwache der Submit-Weiche wiederverwenden. Hier KEIN Sicherheitsschloss
	// (es wird nichts weitergeleitet), sondern ein Request-Sparer: Textsuchen
	// ("Golf 7", "ABS Steuergerät") haben in den Metaobjekten nichts zu holen
	// und lösen so keinen einzigen Backend-Request aus. Nach dem Cold-Cache-
	// Stampede (Suche 5 Tage tot) will man /search nicht pauschal aufschalten.
	// Bekannte Lücke: 'q=01130 - 046A' — der komplette Fehlercode-NAME, wie ihn
	// ein Kunde aus der Seite kopiert — fällt durch (Token '-' ohne Ziffer).
	if (!instance.looksLikeTeilenummer(searchTerm)) return;

	// Breaker LESEN ja, ARMIEREN nein. fetchMetaobjectResults() setzt bei jedem
	// Fehlschlag metaobjectDownUntil = jetzt + 60s auf der Instanz — und das ist
	// genau die Instanz, deren Suchfeld unter "Keine Ergebnisse gefunden" steht.
	// Ein Render-Cold-Start (2500ms-Abort, danach antwortet das Backend wieder in
	// 151ms) würde damit die NÄCHSTE, BEWUSSTE Nutzeraktion bestrafen: die
	// Submit-Weiche stiege 60s lang sofort nativ aus (Z.149) und das Dropdown
	// zeigte 60s keine Metaobjekt-Treffer — obwohl das Backend längst wieder da
	// ist. Die Seite bliebe also NICHT "exakt wie heute", sondern schlechter.
	// Der Breaker aus #833 schützt vor dem Cold-Cache-Stampede durch NUTZER-
	// getriebene Requests; dieser hier ist automatisch und ungefragt. Deshalb:
	// Stand merken und einen NUR von uns ausgelösten Ausschlag zurücknehmen. Ein
	// bereits vorher offener Breaker bleibt unangetastet (fetchMetaobjectResults
	// steigt dann ohnehin sofort aus und rührt den Wert nicht an).
	const breakerVorher = instance.metaobjectDownUntil;

	// Höhe reservieren, BEVOR der Fetch startet — aber nur im >0-Treffer-Fall.
	// Dort ist der Header sonst leer, der Block landet also ganz oben und schöbe
	// beim Eintreffen der Antwort (bis 2500ms nach dem First Paint der bereits
	// server-seitig gerenderten Trefferliste) das komplette Produkt-Grid nach
	// unten — unter dem Finger des Kunden weg. Im 0-Treffer-Fall wird NICHT
	// reserviert: dort verdrängt der Block den Hero, eine Reservierung käme zu
	// dessen Höhe hinzu und schöbe die Suchleiste erst recht.
	// Kein leerer Kasten: die Reservierung ist reiner Raum (kein Rahmen, kein
	// Text) — die Box-Optik hängt an :not(:empty), siehe main-search.liquid.
	// Grenzen, bewusst in Kauf genommen: die Höhe ist auf Überschrift + EINEN
	// Treffer geschätzt (der häufigste Fall — im Volltreffer-Modus unten exakt 1),
	// bei mehreren Treffern bleibt ein Restversatz. Und liefert das Backend nichts
	// Brauchbares, kollabiert der reservierte Raum wieder. Beides ist deutlich
	// kleiner als der ungebremste Sprung. Bei warmem Cache/offenem Breaker löst
	// das Promise in einer Microtask auf -> die Reservierung lebt < 1 Frame und
	// wird nie sichtbar.
	if (!rfVerdraengtHero(mount)) {
		mount.setAttribute('data-pending', '');
	}

	// Die vorhandene Instanz füttert den Block: Cache, In-flight-Dedup,
	// Circuit-Breaker (#833, hat einen 5-Tage-Totalausfall behoben) und der
	// 2500ms-Abort gelten damit unverändert. Kein zweiter, ungebremster
	// fetch-Pfad daneben.
	instance.fetchMetaobjectResults(searchTerm)
		.then((result) => {
			rfAufraeumen(mount, instance, breakerVorher);

			const data = (result && result.data) || { results: [] };
			const kandidaten = (data.results || []).filter((item) => (
				item &&
				item.score >= RF_SUGGEST_MIN_SCORE &&
				(item.type === 'teilenummer' || item.type === 'fehlercodes') &&
				instance.isInternalUrl(item.url)
			));

			// Volltreffer-Modus über dieselbe Funktion, die auch die Submit-Weiche
			// entscheiden lässt -> Enter-im-Feld und /search?q=... können nicht
			// auseinanderlaufen. Live belegt: q=5K7035200K liefert 10 Treffer, die
			// ALLE auf dasselbe Produkt zeigen (1x exakt + 9x Geschwister-TN).
			// Ungefiltert wären das 10 Karten für EIN Gerät — wer die Nummer auf
			// dem Zettel hat, will eine Antwort, keine Auswahl.
			const exactUrl = instance.findExactTeilenummer(data, searchTerm);
			const exactItem = exactUrl ? kandidaten.find((item) => item.url === exactUrl) : null;
			const treffer = exactItem ? [exactItem] : kandidaten.slice(0, RF_SUGGEST_MAX_ITEMS);

			// Nichts Brauchbares -> gar nichts. Kein Kasten, keine Zeile, keine
			// einzige DOM-Mutation.
			if (!treffer.length) return;

			rfRenderSuggestions(mount, treffer);
		})
		.catch((error) => {
			// fetchMetaobjectResults rejectet nie (eigenes .catch() + Breaker) ->
			// das hier ist reine Absicherung gegen einen Fehler im Rendering.
			// Die Seite bleibt dann exakt wie heute.
			rfAufraeumen(mount, instance, breakerVorher);
			console.warn('[PredictiveSearch] Vorschlagsblock:', error);
		});
}

// Reservierten Raum freigeben und einen allein von unserem automatischen Fetch
// ausgelösten Breaker-Ausschlag zurücknehmen. Beides passiert in DERSELBEN Task
// wie das anschliessende Rendern -> ein einziger Reflow, kein Zwischenzustand.
function rfAufraeumen(mount, instance, breakerVorher) {
	mount.removeAttribute('data-pending');

	if (instance.metaobjectDownUntil > breakerVorher) {
		instance.metaobjectDownUntil = breakerVorher;
	}
}

// Steht der Hero direkt hinter dem Anker, blendet ihn die CSS-Regel
// `#rf-metaobject-suggestions:not(:empty) + .page-hero-empty` aus, sobald der
// Block Inhalt hat -> der Block ERSETZT ihn dann. Dieselbe Adjazenz entscheidet
// über Höhen-Reservierung (nur wenn der Hero NICHT verdrängt wird) und über das
// Überschriften-Tag (h1 nur wenn doch). Die DOM-Nachbarschaft ändert sich
// zwischen Fetch-Start und Rendern nicht, beide Aufrufe sehen dasselbe.
function rfVerdraengtHero(mount) {
	const naechster = mount.nextElementSibling;
	return !!naechster && naechster.classList.contains('page-hero-empty');
}

function rfRenderSuggestions(mount, treffer) {
	// Bewusst createElement/textContent statt Template-String: name, displayType
	// und product.title kommen aus dem Backend. buildMetaobjectSection() escaped
	// per escapeHtml() — korrekt, aber es genügt EIN vergessenes esc() bei der
	// nächsten Erweiterung. textContent kann man nicht vergessen. Der Suchbegriff
	// selbst wird hier gar nicht gerendert.
	// In ein DocumentFragment bauen und EINMAL anhängen: ein halbfertiger Block
	// wird nie sichtbar, und der Layout-Shift bleibt ein einziger.
	const fragment = document.createDocumentFragment();

	// Überschriften-Tag am Kontext ausrichten, nicht fest h2. Verdrängt der Block
	// den Hero (0-Treffer-Fall), nimmt er dessen <h1> "Keine Ergebnisse gefunden"
	// mit — und die einzige andere h1 der Seite (.page-title) rendert nur bei
	// search.performed == false, search.json enthält ausser main-search nichts.
	// Ein festes h2 liesse die Seite also ausgerechnet im ERFOLGSFALL ganz ohne
	// h1 zurück, obwohl sie heute eine hat: der Sprung zur h1 (NVDA: Taste 1)
	// fände nichts. Ersetzt der Block die Seitenüberschrift, ist h1 das korrekte
	// Tag. Im >0-Treffer-Fall bleibt h2 richtig — dort ersetzt er nichts.
	// Die Klassen bleiben identisch -> die Optik ändert sich nicht.
	const heading = document.createElement(rfVerdraengtHero(mount) ? 'h1' : 'h2');
	heading.className = 'predictive-search-heading predictive-search-heading--metaobject h5';
	heading.textContent = rfSuggestionsHeading(treffer);
	fragment.appendChild(heading);

	const list = document.createElement('ul');
	list.className = 'predictive-search-results-item-list';
	// WARUM NICHT buildMetaobjectSection() (Auflage "wiederverwenden statt
	// duplizieren", bewusst abgewogen): die Funktion ist an die Listbox des
	// Dropdowns gekoppelt und liefert role="option", aria-selected und feste IDs
	// (predictive-search-option-<type>-<index>). Auf /search hiesse das
	// role="option" ohne Listbox (ungültiges ARIA) und im 0-Treffer-Fall
	// DOPPELTE DOM-IDs neben der zweiten <predictive-search>-Instanz, die deren
	// aria-activedescendant/switchOption() vergiften. Wiederverwendet wird
	// stattdessen alles, was tatsächlich kontextfrei ist: fetchMetaobjectResults,
	// looksLikeTeilenummer, findExactTeilenummer, isInternalUrl — plus die
	// komplette Item-CSS (component-predictive-search.css Z.311-362, geprüft:
	// kein position, kein Vorfahren-Selektor). Kehrseite dieser CSS-Kopplung:
	// wer die Metaobjekt-Spalte im Dropdown restylt, verschiebt auch /search.
	// Gewollt (eine Optik), aber nicht offensichtlich.

	treffer.forEach((item) => {
		const li = document.createElement('li');
		li.className = 'predictive-search-list-item predictive-search-list-item--metaobject';

		const link = document.createElement('a');
		link.className = 'predictive-search-item predictive-search-item-link predictive-search-item--metaobject';
		// isInternalUrl() ist oben schon gefallen -> nur interne Pfade. Items mit
		// feindlicher URL (javascript:, //evil.com, /\evil.com) werden gar nicht
		// erst gerendert, statt sie hier zu entschärfen.
		link.href = item.url;

		const content = document.createElement('div');
		content.className = 'predictive-search-item-content';

		const badge = document.createElement('span');
		badge.className = 'predictive-search-item-type ' + RF_SUGGEST_BADGE_CLASS[item.type];
		badge.textContent = item.displayType || '';
		content.appendChild(badge);

		const name = document.createElement('h3');
		name.className = 'predictive-search-item-heading typography-body';
		name.textContent = item.name || '';
		content.appendChild(name);

		// Bewusst KEIN Produktfoto: <img src> aus Backend-Daten wäre eine neue
		// Vertrauensfläche, die das Dropdown heute nicht hat. Der Produktname als
		// Text leistet die Wiedererkennung ohne sie.
		// Und nur bei teilenummer: ein Fehlercode gilt für mehrere Geräte (01130 ->
		// MK20/MK26/MK60/MK61), das Backend liefert aber nur EIN product — es
		// darunterzuschreiben behauptete eine Eindeutigkeit, die es nicht gibt.
		if (item.type === 'teilenummer' && item.product && item.product.title) {
			const produkt = document.createElement('span');
			produkt.className = 'predictive-search-item-vendor';
			produkt.textContent = item.product.title;
			content.appendChild(produkt);
		}

		link.appendChild(content);
		li.appendChild(link);
		list.appendChild(li);
	});

	fragment.appendChild(list);

	// role/aria-label erst JETZT, zusammen mit dem Inhalt: ein server-seitig
	// gesetztes role="region" wäre auf jeder /search-Seite eine LEERE Landmarke.
	// Bewusst kein role="status": über 7 nativen Produkten läse ein Screenreader
	// sonst bis zu 5 Einträge am Stück vor. Ob im 0-Treffer-Fall umgekehrt eine
	// Ansage gewünscht ist, ist eine offene Produktfrage (Deploy-Notiz).
	mount.setAttribute('role', 'region');
	mount.setAttribute('aria-label', heading.textContent);
	mount.appendChild(fragment);
}

function rfSuggestionsHeading(treffer) {
	// Deutsch fest verdrahtet — wie im Bestand (buildMetaobjectColumn Z.541/545).
	const hatTn = treffer.some((item) => item.type === 'teilenummer');
	const hatFc = treffer.some((item) => item.type === 'fehlercodes');

	if (hatTn && hatFc) return 'Passende Teilenummern und Fehlercodes';
	return hatFc ? 'Passende Fehlercodes' : 'Passende Teilenummern';
}

// defer läuft nach dem Parsen und vor DOMContentLoaded (readyState dann
// 'interactive') -> der direkte Aufruf reicht. Die Klasse ist oben definiert und
// customElements.define() ist [CEReactions]-annotiert, arbeitet seine
// Upgrade-Queue also synchron vor dem Return ab: das Element ist hier bereits
// upgegradet, ein customElements.whenDefined() wäre reine Zeremonie (nachgestellt
// in Chromium: typeof el.fetchMetaobjectResults === 'function' ist direkt nach
// define() true). Die readyState-Weiche kostet 5 Zeilen und hält, falls das
// Script je aus dem defer-Pfad wandert.
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', rfInitSearchPageSuggestions);
} else {
	rfInitSearchPageSuggestions();
}
